import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled/theme'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  AgentLanguage,
  AgentLanguageValue
} from '../agent-language/agent-language'
import {
  AgentFeature,
  AgentFeatureName,
  isFeatureEnabledForAllLanguages
} from '../agent-features'
import { getIsBetaRule } from '../protect-rules/beta-protect-rules'
import { getIsNewRule } from '../protect-rules/new-protect-rules'
import { NewRuleBadge } from '../new-rule-badge/new-rule-badge'

export const languagesDataTestID = 'language-label'

export interface LanguagesLabelProps {
  readonly ruleUuid: string
  readonly languages: AgentLanguageValue[]
  readonly featureName: AgentFeatureName
  readonly agentFeatures: AgentFeature[]
  readonly dataTestId?: string
}

export function LanguageLabel({
  ruleUuid,
  languages,
  agentFeatures,
  featureName,
  dataTestId = languagesDataTestID
}: LanguagesLabelProps) {
  const { formatMessage } = useIntl()
  const ruleLanguages = languages.map((lang) => AgentLanguage.fromValue(lang))
  const isAllLanguages = isFeatureEnabledForAllLanguages({
    agentFeatures,
    featureName,
    languages: ruleLanguages
  })
  const isAnyBeta = ruleLanguages.some((lang) => getIsBetaRule(ruleUuid, lang))
  const isAnyNew = ruleLanguages.some((lang) => getIsNewRule(ruleUuid, lang))

  return (
    <div
      data-testid={dataTestId}
      className={css`
        font-size: 12px;
        color: ${theme.color.neutral50};
      `}
    >
      {isAllLanguages && !isAnyBeta && !isAnyNew && (
        <span>{formatMessage({ id: 'detail_allApplicationLanguages' })}</span>
      )}
      {!isAllLanguages &&
        ruleLanguages.map((lang, index) => (
          <span key={lang.getName()}>
            {`${lang.getDisplay()}`}
            {getIsNewRule(ruleUuid, lang) && (
              <span
                className={css`
                  margin-left: 4px;
                `}
              >
                <NewRuleBadge />
              </span>
            )}
            {getIsBetaRule(ruleUuid, lang) && <BetaTag />}
            {index < ruleLanguages.length - 1 ? ', ' : ''}
          </span>
        ))}
    </div>
  )
}

function BetaTag() {
  const { formatMessage } = useIntl()
  return (
    <span
      className={css`
        font-size: 10px;
        font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
      `}
    >
      {` (${formatMessage({ id: 'detail_beta' })})`}
    </span>
  )
}
